import * as Yup from "yup";
import {
  PATIENT_VALIDATION_PATTERNS,
  PATIENT_FIELD_LIMITS,
  PATIENT_VALIDATION_MESSAGES,
  CreatePatientFormData,
} from "./constants";

// ===== PATIENT CREATION VALIDATION SCHEMA =====

/**
 * Schema de validación para el formulario de creación de pacientes
 * Basado en las imágenes de referencia y patrones existentes de MedApp
 */
export const createPatientValidationSchema = Yup.object({
  firstName: Yup.string()
    .matches(
      PATIENT_VALIDATION_PATTERNS.NAME_PATTERN,
      PATIENT_VALIDATION_MESSAGES.firstName.pattern
    )
    .min(
      PATIENT_FIELD_LIMITS.FIRST_NAME_MIN,
      PATIENT_VALIDATION_MESSAGES.firstName.minLength
    )
    .max(
      PATIENT_FIELD_LIMITS.FIRST_NAME_MAX,
      PATIENT_VALIDATION_MESSAGES.firstName.maxLength
    )
    .required(PATIENT_VALIDATION_MESSAGES.firstName.required),

  lastName: Yup.string()
    .matches(
      PATIENT_VALIDATION_PATTERNS.NAME_PATTERN,
      PATIENT_VALIDATION_MESSAGES.lastName.pattern
    )
    .min(
      PATIENT_FIELD_LIMITS.LAST_NAME_MIN,
      PATIENT_VALIDATION_MESSAGES.lastName.minLength
    )
    .max(
      PATIENT_FIELD_LIMITS.LAST_NAME_MAX,
      PATIENT_VALIDATION_MESSAGES.lastName.maxLength
    )
    .required(PATIENT_VALIDATION_MESSAGES.lastName.required),

  email: Yup.string()
    .matches(
      PATIENT_VALIDATION_PATTERNS.EMAIL_PATTERN,
      PATIENT_VALIDATION_MESSAGES.email.format
    )
    .max(
      PATIENT_FIELD_LIMITS.EMAIL_MAX,
      PATIENT_VALIDATION_MESSAGES.email.maxLength
    )
    .required(PATIENT_VALIDATION_MESSAGES.email.required),
});

// ===== HELPER TYPES =====

/**
 * Tipo para los valores del formulario
 */
export type PatientFormValues = CreatePatientFormData;

// ===== HELPER FUNCTIONS =====

/**
 * Valores iniciales para el formulario
 */
export const initialPatientFormValues: PatientFormValues = {
  firstName: "",
  lastName: "",
  email: "",
};

/**
 * Función helper para validar un campo específico
 */
export const validateField = async (
  fieldName: keyof PatientFormValues,
  value: string
): Promise<string | null> => {
  try {
    await createPatientValidationSchema.validateAt(fieldName, {
      [fieldName]: value,
    });
    return null;
  } catch (error) {
    if (error instanceof Yup.ValidationError) {
      return error.message;
    }
    return "Error de validación";
  }
};

/**
 * Función helper para validar todo el formulario
 */
export const validateForm = async (
  values: PatientFormValues
): Promise<{ [key: string]: string } | null> => {
  try {
    await createPatientValidationSchema.validate(values, { abortEarly: false });
    return null;
  } catch (error) {
    if (error instanceof Yup.ValidationError) {
      const validationErrors: { [key: string]: string } = {};
      error.inner.forEach((err) => {
        if (err.path) {
          validationErrors[err.path] = err.message;
        }
      });
      return validationErrors;
    }
    return { form: "Error de validación del formulario" };
  }
};

/**
 * Función helper para limpiar y formatear datos antes del envío
 */
export const sanitizePatientData = (
  values: PatientFormValues
): PatientFormValues => {
  return {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    email: values.email.trim().toLowerCase(),
  };
};

/**
 * Función helper para verificar si el formulario tiene errores
 */
export const hasFormErrors = (
  errors: { [key: string]: string } | undefined
): boolean => {
  return errors ? Object.keys(errors).length > 0 : false;
};

/**
 * Función helper para obtener mensaje de error específico por campo
 */
export const getFieldErrorMessage = (
  fieldName: keyof PatientFormValues,
  error?: string
): string => {
  if (!error) return "";

  // Si el error viene del backend, devolverlo directamente
  if (
    error.includes("ya está registrado") ||
    error.includes("already exists")
  ) {
    return PATIENT_VALIDATION_MESSAGES.email.unique || error;
  }

  return error;
};

/**
 * Función helper para verificar si un email es válido sin ejecutar toda la validación
 */
export const isValidEmail = (email: string): boolean => {
  return PATIENT_VALIDATION_PATTERNS.EMAIL_PATTERN.test(email);
};

/**
 * Función helper para verificar si un nombre es válido sin ejecutar toda la validación
 */
export const isValidName = (name: string): boolean => {
  return PATIENT_VALIDATION_PATTERNS.NAME_PATTERN.test(name);
};

/**
 * Función helper para obtener el estado de validación de todos los campos
 */
export const getFieldsValidationState = (values: PatientFormValues) => {
  return {
    firstName: {
      isValid: isValidName(values.firstName),
      isEmpty: values.firstName.trim().length === 0,
    },
    lastName: {
      isValid: isValidName(values.lastName),
      isEmpty: values.lastName.trim().length === 0,
    },
    email: {
      isValid: isValidEmail(values.email),
      isEmpty: values.email.trim().length === 0,
    },
  };
};

/**
 * Función helper para verificar si el formulario completo es válido
 */
export const isFormValid = (values: PatientFormValues): boolean => {
  const state = getFieldsValidationState(values);
  return (
    state.firstName.isValid &&
    state.lastName.isValid &&
    state.email.isValid &&
    !state.firstName.isEmpty &&
    !state.lastName.isEmpty &&
    !state.email.isEmpty
  );
};
